package fr.frux.feature.map

import fr.frux.core.common.logging.Logger
import fr.frux.core.maps.Location
import fr.frux.shared.domain.model.MapFilter
import fr.frux.shared.domain.repository.TreeRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update

private const val TAG = "NearestDistanceLoader"

/**
 * Zone vide (#502) : cherche l'arbre le plus proche au rayon plafond, limite 1, sans toucher au
 * lot ni a la camera, et pose sa distance dans [MapState.nearestDistanceMeters]. Le serveur trie
 * par distance, le premier recu est le plus proche. Sorti de [MapViewModel] comme
 * [LocationController] : la classe est sous ratchet detekt `LargeClass`.
 */
internal class NearestDistanceLoader(
    private val treeRepository: TreeRepository,
    private val launchForUser: (suspend CoroutineScope.() -> Unit) -> Job,
    private val state: MutableStateFlow<MapState>,
) {
    private var job: Job? = null

    /** Annule la requete en vol : une reponse tardive ne doit jamais s'afficher sur un lot peuple. */
    fun cancel() {
        job?.cancel()
    }

    fun load(center: Location) {
        cancel()
        job =
            launchForUser {
                treeRepository
                    .getNearbyTrees(
                        lat = center.latitude,
                        lon = center.longitude,
                        radius = MAX_QUERY_RADIUS_KM,
                        filter = MapFilter.None,
                        limit = 1,
                    ).collect { result ->
                        result.onSuccess { nearby ->
                            val nearest = nearby.trees.firstOrNull() ?: return@onSuccess
                            val distance = center.distanceMetersTo(Location(nearest.latitude, nearest.longitude))
                            state.update {
                                // Le centre a pu changer entre-temps : ne pas annoter une autre zone.
                                if (it.queryCenter == center && it.emptyArea != null) {
                                    it.copy(nearestDistanceMeters = distance)
                                } else {
                                    it
                                }
                            }
                        }
                        // Echec : la ligne reste absente, la carte a deja son propre etat d'erreur.
                        result.onFailure { Logger.d(TAG, "Plus proche indisponible : ${it.message}") }
                    }
            }
    }
}
